import React, { useState } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import {
  Breadcrumbs,
  Link,
  Typography,
  Paper,
  Box,
  Stack,
  Grid,
  CircularProgress
} from '@mui/material';
import { FieldArray, Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import * as yup from 'yup';

import { ROUTES } from '../../../../constants/routes';
import { CustomInput } from '../../../../components/CustomInput';
import { CustomInputWithMask } from '../../../../components/CustomInputWithMask';
import { CustomButton } from '../../../../components/CustomButton';
import { ImageUploader } from '../../../../components/ImageUploader';
import { getCompany, updateCompany, uploadCompanyLogo } from '../../../../store/company/actions';
import { REGULAR_EXPRESSIONS } from '../../../bonuses/constants';

export const EditCompany = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const companyState = useSelector((state) => state.company);
  const { companyInfo } = companyState;
  const [userAvatar, setUserAvatar] = useState(companyInfo.avatar);

  const validationSchema = yup.object({
    name: yup
      .string()
      .min(2, 'Минимум 2 символа')
      .max(50, 'Максимум 50 символов')
      .test('symbols', 'Допустимые символы: 0-9, Ая-Яя, Aa-Zz, ", -, _, @, пробел', val => /^[ а-яА-Яa-zA-Z0-9-_@"]+$/.test(val))
      .required('Обязательное поле'),
    bin: yup
      .string()
      .min(12, 'Недопустимый формат ввода данных')
      .max(12, 'Недопустимый формат ввода данных')
      .required('Обязательное поле'),
    contact_person: yup
      .string()
      .min(2, 'Минимум 2 символа')
      .max(50, 'Максимум 50 символов')
      .test('symbols', 'Допустимы только буквы кириллицы', val => {if (val) return /^[ а-яА-Я]+$/.test(val); else return true;}),
    city: yup.string().test('symbols', 'Допустимы только буквы кириллицы', val => {if (val) return /^[ а-яА-Я-]+$/.test(val); else return true;}),
    phones: yup.array().of(
      yup.string().test('len', 'Must be exactly 15 characters', val => {
        return val?.length === 15 || val === undefined;
      })
    ),
    email: yup.string().test('email', 'Некорректный email', val => {if (val) return REGULAR_EXPRESSIONS.email_validation_pattern.test(val); else return true;})
  });

  if (!companyInfo?.id) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <CircularProgress data-testid="CircularProgress"/>
      </Box>
    );
  }

  return (
    <>
      <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1">
        Редактировать мою компанию
      </Typography>
      <Breadcrumbs mb="24px" aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.COMPANY}>
          Моя компания
        </Link>
        <Typography color="text.secondary">Редактировать</Typography>
      </Breadcrumbs>
      <Paper
        sx={{
          padding: '24px'
        }}>
        <Formik
          initialValues={{
            name: companyInfo.name,
            bin: companyInfo.bin,
            contact_person: companyInfo.contact_person || '',
            email: companyInfo.email || '',
            phones: (companyInfo.phones?.length && companyInfo.phones) || [''],
            city: companyInfo.city || '',
            logo: companyInfo.logo
          }}
          validationSchema={validationSchema}
          validateOnChange
          onSubmit={(values) => {
            const transformData = {
              ...values,
              phones: values.phones.filter(item => item)
            };

            dispatch(updateCompany(transformData));

            let formData = new FormData();
            formData.append('uploaded_file', userAvatar);
            dispatch(uploadCompanyLogo(companyInfo.id, formData));

            setTimeout(() => {
              dispatch(getCompany());
              history.push(ROUTES.COMPANY);
            }, 1000);
          }}>
          {({ values, handleChange, setFieldValue, errors, isValid, dirty }) => (
            <Form>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: '24px',
                  maxWidth: '500'
                }}>
                <Box sx={{ display: 'flex', marginBottom: '24px' }}>
                  <Box
                    sx={{
                      marginRight: '24px'
                    }}>
                    <ImageUploader
                      name="avatar"
                      avatar={companyInfo.logo}
                      setFieldValueWithoutForm={setUserAvatar}
                      setFieldValue={setFieldValue}
                      isCompany
                    />
                  </Box>

                  <Stack spacing={2} justifyContent="center">
                    <CustomInput
                      name="name"
                      value={values.name}
                      required
                      placeholder="Наименование *"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 344 }}
                      size="small"
                      error={errors['name']}
                      helperText={errors['name']}
                    />
                    <CustomInput
                      name="bin"
                      required
                      value={values.bin}
                      placeholder="БИН *"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 344 }}
                      size="small"
                      type="number"
                      onInput={(e) => {
                        e.target.value = Math.max(0, parseInt(e.target.value))
                          .toString()
                          .slice(0, 12);
                      }}
                      error={errors['bin']}
                      helperText={errors['bin']}
                    />
                  </Stack>
                </Box>
                <Grid container spacing={2} mb="24px">
                  <Grid item>
                    <CustomInput
                      name="contact_person"
                      value={values.contact_person}
                      placeholder="Контактное лицо"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 496 }}
                      size="small"
                      helperText={errors['contact_person']}
                      setFieldValue={setFieldValue}
                      error={errors['contact_person']}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} mb="24px">
                  <Grid item>
                    <CustomInput
                      name="email"
                      value={values.email}
                      placeholder="Email"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 496 }}
                      size="small"
                      error={errors['email']}
                      helperText={errors['email']}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sm={6} mb="24px">
                  <FieldArray
                    name="phones"
                    render={(arrayHelpers) => (
                      <>
                        {values?.phones.map((phone, index) => (
                          <Grid item key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: 240 }}>
                              <CustomInputWithMask
                                name={`phones.${index}`}
                                value={phone}
                                placeholder="Номер телефона"
                                onChange={handleChange}
                                onKeyDown={(event) => {if (!phone && event.key === 'Backspace' && values.phones.length !== 1) arrayHelpers.remove(index)}}
                                variant="outlined"
                                sx={{ width: 240 }}
                                size="small"
                              />
                              {values.phones.length === index + 1 && index < 4 && (
                                <AddIcon
                                  sx={{ color: 'primary.main' }}
                                  onClick={() => arrayHelpers.push()}
                                  data-testid="AddPhone"
                                />
                              )}
                            </Box>
                          </Grid>
                        ))}
                      </>
                    )}
                  />
                </Grid>
                <Grid container spacing={2}>
                  <Grid item>
                    <CustomInput
                      name="city"
                      value={values.city}
                      placeholder="Город"
                      onChange={handleChange}
                      variant="outlined"
                      size="small"
                      sx={{ width: 496 }}
                      setFieldValue={setFieldValue}
                      error={errors['city']}
                      helperText={errors['city']}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                <Box>
                  <CustomButton
                    component={RouterLink}
                    to={ROUTES.COMPANY}
                    color="secondary"
                    variant="contained"
                    sx={{ marginRight: '16px' }}>
                    Отмена
                  </CustomButton>
                </Box>
                <CustomButton disabled={!dirty || !isValid} type="submit" variant="contained">
                  Сохранить
                </CustomButton>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </>
  );
};
